import { useState } from "react";
import { useSearchParams } from "react-router";

import { Badge } from "@/components/badge";
import { type Column, DataTable } from "@/components/data-table";
import { Button, Field, Select, TextInput } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import {
	useTaxonomyEditor,
	useTaxonomyWorkspace,
} from "@/features/plausibility/use-taxonomy-editor";
import { useResolveUnclassified } from "@/features/plausibility/use-unclassified";

import { ClassEditor } from "./ClassEditor";
import { DraftBar } from "./DraftBar";
import { SpecEditor } from "./SpecEditor";
import {
	changedFields,
	type ClassRow,
	classRows,
	EMPTY_CLASS,
	EMPTY_SPEC,
	lookAlikeOf,
	SPEC_SOURCE_LABELS,
} from "./taxonomy-view";

type Filter = "alertable" | "all" | "benign" | "inactive" | "problems";

const NEW = "__new__";

/**
 * The verification taxonomy: every class with its description, confusers,
 * costs, and prior, and each alertable class's features. Edits are staged in one
 * shared draft and reach the verifier, for every organization, only when the
 * draft is published. The database is the source of truth; Export writes the
 * in-code seed files a new environment starts from.
 */
export const Taxonomy = () => {
	const workspace = useTaxonomyWorkspace();
	const editor = useTaxonomyEditor();
	// Arriving from an unclassified alert: open a new class prefilled from it,
	// and resolve that alert once the class is saved.
	const [searchParams, setSearchParams] = useSearchParams();
	const [fromAlert] = useState(() =>
		searchParams.get("new")
			? {
					alertId: searchParams.get("from") ?? undefined,
					fields: {
						...EMPTY_CLASS,
						alertable: true,
						description: searchParams.get("description") ?? "",
						displayName: searchParams.get("name") ?? "",
					},
				}
			: undefined,
	);
	const resolveAlert = useResolveUnclassified();
	const [selected, setSelected] = useState<null | string>(
		fromAlert ? NEW : null,
	);
	const [filter, setFilter] = useState<Filter>("all");
	const [search, setSearch] = useState("");
	const [published, setPublished] = useState<string>();
	// Alertable as ticked in the open class editor, before it is saved. Keyed
	// to that editor instance, so a save or undo (which remounts it) drops it.
	const [alertableEdit, setAlertableEdit] = useState<null | {
		editorKey: string;
		value: boolean;
	}>(null);

	const data = workspace.data;
	const rows = data ? classRows(data) : [];
	const busy =
		editor.saveClass.isPending ||
		editor.saveSpec.isPending ||
		editor.revertClass.isPending ||
		editor.revertSpec.isPending ||
		editor.publish.isPending ||
		editor.discard.isPending;

	const term = search.trim().toLowerCase();
	const visible = rows.filter((row) => {
		const matches =
			!term ||
			row.classId.includes(term) ||
			row.fields.displayName.toLowerCase().includes(term) ||
			(row.fields.description?.toLowerCase().includes(term) ?? false);
		if (!matches) return false;
		switch (filter) {
			case "alertable":
				return row.fields.active && row.fields.alertable;
			case "benign":
				return row.fields.active && !row.fields.alertable;
			case "inactive":
				return !row.fields.active;
			case "problems":
				return (
					row.issues.length > 0 ||
					(row.fields.active &&
						row.fields.alertable &&
						(row.specSource === "missing" || row.specSource === "inactive"))
				);
			default:
				return true;
		}
	});

	const classOptions = rows.map((row) => ({
		label: `${row.classId} — ${row.fields.displayName}`,
		value: row.classId,
	}));
	const current = rows.find((row) => row.classId === selected);
	const classEditorKey = current
		? `class:${current.classId}:${JSON.stringify(current.fields)}`
		: "";
	const showFeatures = current
		? alertableEdit?.editorKey === classEditorKey
			? alertableEdit.value
			: current.fields.alertable
		: false;
	const referencedBy = current ? lookAlikeOf(rows, current.classId) : [];

	const columns: Column<ClassRow>[] = [
		{
			header: "Class",
			key: "class",
			render: (row) => (
				<div>
					<code className="text-xs">{row.classId}</code>
					<div className="text-secondary text-xs">{row.fields.displayName}</div>
				</div>
			),
		},
		{
			header: "Kind",
			key: "kind",
			render: (row) => (
				<div className="flex flex-wrap gap-1">
					<Badge
						label={row.fields.alertable ? "alertable" : "benign"}
						value={row.fields.alertable ? "alertNow" : "lowPriorityLog"}
					/>
					{!row.fields.active && <Badge label="inactive" value="skipped" />}
					{row.state !== "live" && (
						<Badge label={`${row.state} in draft`} value="pending" />
					)}
				</div>
			),
		},
		{
			header: "Description",
			key: "description",
			render: (row) => (
				<span
					className={
						row.fields.active && !row.fields.description
							? "text-error-primary text-xs"
							: "text-secondary text-xs"
					}
				>
					{row.fields.description ?? "No description"}
				</span>
			),
		},
		{
			header: "Confusers",
			key: "confusers",
			render: (row) => (
				<span className="text-secondary text-xs">
					{row.fields.confusers.join(", ") || "—"}
				</span>
			),
		},
		{
			header: "Features",
			key: "features",
			render: (row) =>
				row.fields.alertable ? (
					<span
						className={
							row.fields.active &&
							(row.specSource === "missing" || row.specSource === "inactive")
								? "text-error-primary text-xs"
								: "text-secondary text-xs"
						}
					>
						{row.spec?.features.length ?? 0} ·{" "}
						{SPEC_SOURCE_LABELS[row.specSource]}
					</span>
				) : (
					<span className="text-tertiary text-xs">—</span>
				),
		},
		{
			header: "Edited",
			key: "edited",
			render: (row) =>
				row.live?.userEdited ? (
					<span className="text-secondary text-xs">by staff</span>
				) : (
					<span className="text-tertiary text-xs">seed</span>
				),
		},
	];

	const review = (data?.draft?.classes ?? [])
		.map((change) => ({
			key: `class:${change.classId}`,
			label: `${change.classId}: ${changedFields(change.base, change.value).join(", ")}`,
			onRevert: () => editor.revertClass.mutate(change.classId),
		}))
		.concat(
			(data?.draft?.specs ?? []).map((change) => ({
				key: `spec:${change.classId}`,
				label: `${change.classId} features: ${changedFields(change.base, change.value).join(", ")}`,
				onRevert: () => editor.revertSpec.mutate(change.classId),
			})),
		);

	return (
		<section className="flex flex-col gap-4">
			<div className="flex flex-wrap items-start gap-3">
				<div>
					<h2 className="text-primary text-base font-semibold">Taxonomy</h2>
					<p className="text-secondary text-sm">
						Classes, their descriptions, and the features the verifier looks
						for. Shared by every organization. Admin database
						{data?.versions.taxonomy !== undefined &&
							` · taxonomy v${data.versions.taxonomy}, features v${data.versions.discriminators ?? 0}`}
						.
					</p>
				</div>
				<div className="ml-auto flex items-center gap-2">
					<Button
						disabled={!data?.seeded || editor.exportSeeds.isPending}
						onClick={() => editor.exportSeeds.mutate()}
						type="button"
						variant="secondary"
					>
						Export seed files
					</Button>
					<Button
						disabled={!data}
						onClick={() => setSelected(NEW)}
						type="button"
					>
						New class
					</Button>
				</div>
			</div>
			{editor.exportSeeds.error && (
				<p className="text-error-primary text-sm" role="alert">
					{editor.exportSeeds.error.message}
				</p>
			)}

			<StatusMessage
				error={workspace.error}
				isLoading={workspace.isLoading}
				loading="Loading taxonomy…"
			/>

			{data && !data.seeded && (
				<p className="text-warning-primary text-sm" role="alert">
					The taxonomy tables have not been seeded, so the verifier is reading
					the in-code seed. Seed them from Global priors before publishing
					changes.
				</p>
			)}

			{data && (
				<DraftBar
					busy={busy}
					confirmMessage="Publish these changes? Every organization's next verification uses them."
					draft={data.draft}
					error={editor.publish.error ?? editor.discard.error}
					issues={data.issues}
					onDiscard={() => editor.discard.mutate()}
					onPublish={() =>
						editor.publish.mutate(undefined, {
							onSuccess: (result) =>
								setPublished(
									`Published ${result.classes} class${result.classes === 1 ? "" : "es"} and ${result.specs} feature spec${result.specs === 1 ? "" : "s"}.`,
								),
						})
					}
					published={published}
					review={review}
				/>
			)}

			{selected === NEW && data && (
				<section className="border-secondary rounded-md border p-4">
					<div className="mb-3 flex items-center">
						<h3 className="text-primary text-sm font-semibold">New class</h3>
						<Button
							className="ml-auto"
							onClick={() => setSelected(null)}
							type="button"
							variant="secondary"
						>
							Close
						</Button>
					</div>
					<ClassEditor
						busy={busy}
						classOptions={classOptions}
						error={editor.saveClass.error}
						onSave={(classId, fields) =>
							editor.saveClass.mutate(
								{ classId, create: true, fields },
								{
									onSuccess: () => {
										setSelected(classId);
										if (fromAlert?.alertId) {
											resolveAlert.mutate({
												classId,
												ids: [fromAlert.alertId],
											});
											setSearchParams({}, { replace: true });
										}
									},
								},
							)
						}
						staged={false}
						value={fromAlert?.fields ?? EMPTY_CLASS}
					/>
				</section>
			)}

			{current && data && (
				<section className="border-secondary flex flex-col gap-4 rounded-md border p-4">
					<div className="flex items-center gap-2">
						<h3 className="text-primary text-sm font-semibold">
							{current.fields.displayName || current.classId}
						</h3>
						<code className="text-tertiary text-xs">{current.classId}</code>
						<Button
							className="ml-auto"
							onClick={() => setSelected(null)}
							type="button"
							variant="secondary"
						>
							Close
						</Button>
					</div>
					{current.issues.length > 0 && (
						<ul
							className="text-error-primary list-disc pl-5 text-sm"
							role="alert"
						>
							{current.issues.map((issue) => (
								<li key={issue.message}>{issue.message}</li>
							))}
						</ul>
					)}
					<ClassEditor
						busy={busy}
						classId={current.classId}
						classOptions={classOptions}
						error={editor.saveClass.error ?? editor.revertClass.error}
						key={classEditorKey}
						onAlertableChange={(value) =>
							setAlertableEdit({ editorKey: classEditorKey, value })
						}
						onRevert={() => editor.revertClass.mutate(current.classId)}
						onSave={(classId, fields) =>
							editor.saveClass.mutate({ classId, create: false, fields })
						}
						staged={current.state !== "live"}
						value={current.fields}
					/>

					{showFeatures ? (
						<div>
							<h4 className="text-primary mb-1 text-sm font-semibold">
								Features
							</h4>
							<p className="text-secondary mb-3 text-xs">
								What the anchored pass checks, one observation per feature.
								{current.specSource === "seed" &&
									" No spec row yet: the verifier uses the in-code seed. Saving writes a row."}
							</p>
							{!current.fields.alertable && (
								<p className="text-warning-primary mb-3 text-xs" role="status">
									Alertable is not saved yet. Save the class to the draft as
									well, or these features are never read.
								</p>
							)}
							<SpecEditor
								busy={busy}
								classOptions={classOptions.filter(
									(option) => option.value !== current.classId,
								)}
								error={editor.saveSpec.error ?? editor.revertSpec.error}
								key={`spec:${current.classId}:${JSON.stringify(current.spec)}`}
								lockedKeys={current.lockedKeys}
								onRevert={() => editor.revertSpec.mutate(current.classId)}
								onSave={(fields) =>
									editor.saveSpec.mutate({ classId: current.classId, fields })
								}
								prefix={current.classId}
								staged={current.specState !== "live"}
								value={current.spec ?? EMPTY_SPEC}
							/>
						</div>
					) : (
						<div>
							<h4 className="text-primary mb-1 text-sm font-semibold">
								Features
							</h4>
							<p className="text-secondary text-xs">
								Benign classes have no features: the verifier only checks
								footage for alertable ones, and uses benign classes to name what
								a clip really shows. The cues that tell this class apart belong
								on the alertable classes it is mistaken for, as features whose
								presence points to a look-alike. Tick Alertable to give this
								class features of its own.
							</p>
							{referencedBy.length > 0 ? (
								<div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
									<span className="text-tertiary">Look-alike of:</span>
									{referencedBy.map((row) => (
										<button
											className="text-brand-600 underline"
											key={row.classId}
											onClick={() => setSelected(row.classId)}
											type="button"
										>
											{row.fields.displayName || row.classId}
										</button>
									))}
								</div>
							) : (
								<p className="text-tertiary mt-2 text-xs">
									No alertable class lists it as a look-alike yet.
								</p>
							)}
						</div>
					)}
				</section>
			)}

			{data && (
				<>
					<div className="flex flex-wrap items-end gap-3">
						<Field htmlFor="taxonomy-search" label="Search">
							<TextInput
								id="taxonomy-search"
								onChange={(event) => setSearch(event.target.value)}
								placeholder="id, name, or description"
								value={search}
							/>
						</Field>
						<Field htmlFor="taxonomy-filter" label="Show">
							<Select
								id="taxonomy-filter"
								onChange={(event) => setFilter(event.target.value as Filter)}
								value={filter}
							>
								<option value="all">all classes</option>
								<option value="alertable">alertable</option>
								<option value="benign">benign</option>
								<option value="inactive">inactive</option>
								<option value="problems">needs attention</option>
							</Select>
						</Field>
						<span className="text-tertiary pb-1 text-xs">
							{visible.length} of {rows.length}
						</span>
					</div>
					<DataTable
						columns={columns}
						onRowClick={(row) => setSelected(row.classId)}
						rowKey={(row) => row.classId}
						rows={visible}
					/>
				</>
			)}
		</section>
	);
};
